import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Sky } from '@react-three/drei';
import * as THREE from 'three';
import { SimulationWorld } from '@tankworld/simulation';
import { useGameStore } from '../../state/game-store.js';
import { inputManager } from '../../input/input-manager.js';
import { networkClient } from '../../network/network-client.js';
import { TerrainMesh } from './TerrainMesh.js';
import { TacticalObstacles } from './TacticalObstacles.js';
import { TankModel } from './TankModel.js';
import { ProjectilesRenderer } from './ProjectilesRenderer.js';
import { FollowCamera } from './FollowCamera.js';

// Simulation controller inside Canvas
const SceneController: React.FC = () => {
  const gameMode = useGameStore((s) => s.gameMode);
  const isPaused = useGameStore((s) => s.isPaused);
  const botDifficulty = useGameStore((s) => s.botDifficulty);
  const localPlayerId = useGameStore((s) => s.localPlayerId);
  const tanks = useGameStore((s) => s.tanks);
  const projectiles = useGameStore((s) => s.projectiles);
  const cameraMode = useGameStore((s) => s.cameraMode);

  // Local simulation world for single-player training
  const localWorld = useMemo(() => new SimulationWorld(), []);
  const localTankId = useRef('player-local');

  // Initialize single-player world
  useEffect(() => {
    if (gameMode === 'singleplayer') {
      localWorld.reset();
      const pTank = localWorld.addVehicle(localTankId.current, 'Commander (You)', '#4b5320', false);
      useGameStore.getState().setLocalPlayerId(localTankId.current);

      // Add training bots
      localWorld.addBot('bot-1', 'Target Bot Alpha', botDifficulty, '#8b2500');
      localWorld.addBot('bot-2', 'Target Bot Bravo', botDifficulty, '#8b2500');
      localWorld.addBot('bot-3', 'Target Bot Charlie', botDifficulty, '#8b2500');

      // Sync initial state
      const initial = localWorld.step(0.001);
      useGameStore.getState().updateWorldSnapshot(initial.tanks, initial.projectiles, initial.events, 0);
    }
  }, [gameMode, botDifficulty, localWorld]);

  // Frame simulation and input loop
  const frameAccumulator = useRef(0);
  const fpsTimer = useRef(performance.now());
  const frameCount = useRef(0);

  useFrame((_, delta) => {
    // FPS measurement
    frameCount.current++;
    const now = performance.now();
    if (now - fpsTimer.current >= 1000) {
      useGameStore.getState().updateDiagnostics(frameCount.current, useGameStore.getState().pingMs);
      frameCount.current = 0;
      fpsTimer.current = now;
    }

    if (isPaused || gameMode === 'menu') return;

    const { input, wantsToFire } = inputManager.pollInput();

    if (gameMode === 'singleplayer') {
      // Step authoritative local simulation at fixed 30Hz
      frameAccumulator.current += Math.min(delta, 0.1);
      const fixedDt = 1 / 30;

      while (frameAccumulator.current >= fixedDt) {
        localWorld.queueInput(localTankId.current, input);
        if (wantsToFire) {
          localWorld.queueFireRequest(localTankId.current);
        }

        const { tanks: sTanks, projectiles: sProj, events: sEvt } = localWorld.step(fixedDt);
        useGameStore.getState().updateWorldSnapshot(sTanks, sProj, sEvt, localWorld.tick);
        frameAccumulator.current -= fixedDt;
      }
    } else if (gameMode === 'multiplayer') {
      // Send inputs to server
      networkClient.sendInput(input);
      if (wantsToFire) {
        networkClient.sendFireRequest(input.sequence);
      }
    }
  });

  const activeTank = tanks.find((t) => t.id === localPlayerId);

  return (
    <>
      <TerrainMesh terrain={localWorld.terrain} />
      <TacticalObstacles obstacles={localWorld.obstacles} />

      {tanks.map((tank) => (
        <TankModel key={tank.id} tank={tank} isSelf={tank.id === localPlayerId} />
      ))}

      <ProjectilesRenderer projectiles={projectiles} />
      <FollowCamera tank={activeTank} cameraMode={cameraMode} terrain={localWorld.terrain} />
    </>
  );
};

export const BattlefieldScene: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCanvasClick = () => {
    if (containerRef.current) {
      inputManager.requestPointerLock(containerRef.current);
    }
  };

  return (
    <div
      ref={containerRef}
      onClick={handleCanvasClick}
      className="absolute inset-0 w-full h-full cursor-crosshair"
    >
      <Canvas
        shadows
        gl={{ antialias: true, powerPreference: 'high-performance' }}
        camera={{ position: [0, 8, -16], fov: 55, near: 0.2, far: 500 }}
      >
        {/* Realistic Lighting & Atmosphere */}
        <ambientLight intensity={0.4} />
        <hemisphereLight args={['#a8c0d8', '#384627', 0.5]} />
        <directionalLight
          position={[60, 90, 40]}
          intensity={1.3}
          castShadow
          shadow-mapSize={[2048, 2048]}
          shadow-camera-left={-120}
          shadow-camera-right={120}
          shadow-camera-top={120}
          shadow-camera-bottom={-120}
          shadow-camera-near={10}
          shadow-camera-far={260}
          shadow-bias={-0.0005}
        />

        {/* Distance Tactical Atmospheric Fog */}
        <fog attach="fog" args={['#202621', 40, 280]} />

        {/* Sky dome with realistic sunlight vector */}
        <Sky
          distance={450000}
          sunPosition={[60, 90, 40]}
          inclination={0.4}
          azimuth={0.25}
          turbidity={8}
          rayleigh={1.2}
        />

        <SceneController />
      </Canvas>
    </div>
  );
};
